/**
 * Modal de revisión de la propuesta IA sobre un pago de tasa.
 * - Informativo: muestra estado, confianza, justificación y fecha detectada.
 * - Editable: el usuario puede CORREGIR el análisis si la IA se equivoca.
 *   Al guardar se marca como revisado (ia_revisado_por + ia_revisado_at).
 * - Acción de descarte: "Rechazar propuesta" limpia todos los campos IA y
 *   también marca el pago como revisado.
 * - Nunca cambia automáticamente el estado del pago (decisión del usuario).
 */
import React, { useState, useEffect } from 'react';
import { Sparkles, XCircle, Calendar, Cpu, Loader2, Save, Pencil, ShieldCheck } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { AnalisisIATasasBadge } from './AnalisisIATasasBadge';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import { obtenerToken } from '../lib/auth';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADOS_IA = [
  { value: 'SE_PUEDE_RECLAMAR',      label: 'Se puede reclamar' },
  { value: 'POSIBLE_NO_RECUPERABLE', label: 'Posible no recuperable' },
  { value: 'PENDIENTE_RESOLUCION',   label: 'Pendiente resolución' },
  { value: 'NO_APLICA',              label: 'No aplica' },
  { value: 'REVISAR_MANUAL',         label: 'Revisar manual' },
];

export const AnalisisIATasasModal = ({ pago, open, onOpenChange, onAfterReject, onAnalizarAhora }) => {
  const [busy, setBusy] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ estado: '', justificacion: '', fecha_res: '' });

  useEffect(() => {
    if (pago) {
      setForm({
        estado: pago.ia_estado_propuesto || 'REVISAR_MANUAL',
        justificacion: pago.ia_justificacion || '',
        fecha_res: pago.ia_fecha_resolucion_detectada
          ? String(pago.ia_fecha_resolucion_detectada).slice(0, 10)
          : '',
      });
      setEditMode(false);
    }
  }, [pago]);

  if (!pago) return null;

  const tieneAnalisis = !!pago.ia_estado_propuesto;
  const yaRevisado    = !!pago.ia_revisado_at;
  const fecha = pago.ia_fecha_resolucion_detectada ? String(pago.ia_fecha_resolucion_detectada).slice(0, 10) : null;

  const handleRechazar = async () => {
    if (!window.confirm('¿Descartar la propuesta IA para este pago? Se registrará como revisada.')) return;
    setBusy(true);
    try {
      const token = obtenerToken();
      await axios.post(
        `${BACKEND_URL}/api/pagos-tasas/${pago.id}/rechazar-propuesta-ia`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Propuesta descartada', description: `Pago #${pago.id} marcado como revisado.` });
      onOpenChange(false);
      onAfterReject?.(pago.id);
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const handleAnalizar = async () => {
    setBusy(true);
    try {
      const token = obtenerToken();
      const r = await axios.post(
        `${BACKEND_URL}/api/pagos-tasas/${pago.id}/analizar-ia`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Análisis completado', description: `Propuesta: ${r.data?.resultado?.estado || '?'}` });
      onAnalizarAhora?.(pago.id);
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const handleGuardarEdicion = async () => {
    if (!form.estado) {
      toast({ title: 'Falta estado', description: 'Selecciona el estado corregido.', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const token = obtenerToken();
      const payload = {
        ia_estado_propuesto: form.estado,
        ia_justificacion: form.justificacion?.trim() || null,
        ia_fecha_resolucion_detectada: form.fecha_res || null,
      };
      await axios.put(
        `${BACKEND_URL}/api/pagos-tasas/${pago.id}/editar-propuesta-ia`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Revisión guardada', description: `Pago #${pago.id} marcado como revisado por ti.` });
      onOpenChange(false);
      onAnalizarAhora?.(pago.id); // recarga los datos del pago y del listado
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="ia-tasas-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            Propuesta IA · Pago #{pago.id}
            {yaRevisado && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-300 rounded-full px-2 py-0.5">
                <ShieldCheck className="w-3 h-3" /> Revisado por usuario
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {pago.tramite} · {Number(pago.importe || 0).toFixed(2)} €
            {pago.expediente?.numero_expediente ? ` · ${pago.expediente.numero_expediente}` : ''}
          </DialogDescription>
        </DialogHeader>

        {tieneAnalisis && !editMode && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <AnalisisIATasasBadge
                estado={pago.ia_estado_propuesto}
                confianza={pago.ia_confianza}
                revisado={yaRevisado}
              />
            </div>

            {pago.ia_justificacion && (
              <div className="rounded-md border bg-gray-50 p-3">
                <div className="text-xs font-semibold text-gray-500 mb-1">Razonamiento</div>
                <div className="text-sm text-gray-800 whitespace-pre-wrap">{pago.ia_justificacion}</div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-xs">
              {fecha && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" /> Fecha resolución detectada: <b>{fecha}</b>
                </div>
              )}
              {pago.ia_modelo_usado && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Cpu className="w-4 h-4" /> Modelo: <b>{pago.ia_modelo_usado}</b>
                </div>
              )}
              {pago.ia_analizado_at && (
                <div className="col-span-2 text-gray-500">
                  Analizado el {String(pago.ia_analizado_at).replace('T', ' ').slice(0, 16)}
                </div>
              )}
              {yaRevisado && (
                <div className="col-span-2 text-green-700">
                  <ShieldCheck className="w-3 h-3 inline mr-1" />
                  Revisado por usuario el {String(pago.ia_revisado_at).replace('T', ' ').slice(0, 16)}
                </div>
              )}
            </div>

            <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
              ℹ️ La propuesta NO cambia el estado del pago. Cuando hagas el trámite con el procurador, marca manualmente el pago como <b>pendiente de devolución</b>.
            </div>
          </div>
        )}

        {tieneAnalisis && editMode && (
          <div className="space-y-3">
            <div className="text-xs text-gray-500">
              Corrige la propuesta de la IA si consideras que es incorrecta. Al guardar, el pago quedará marcado como revisado por ti.
            </div>
            <div>
              <Label className="text-xs">Estado IA (corregido)</Label>
              <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                <SelectTrigger data-testid="edit-ia-estado"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ESTADOS_IA.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Justificación / notas</Label>
              <Textarea
                rows={4}
                value={form.justificacion}
                onChange={(e) => setForm({ ...form, justificacion: e.target.value })}
                placeholder="Motivo de la corrección, referencia a actuación/email, etc."
                data-testid="edit-ia-justificacion"
              />
            </div>
            <div>
              <Label className="text-xs">Fecha resolución detectada (opcional)</Label>
              <Input type="date"
                     value={form.fecha_res}
                     onChange={(e) => setForm({ ...form, fecha_res: e.target.value })}
                     data-testid="edit-ia-fecha-res" />
            </div>
          </div>
        )}

        {!tieneAnalisis && (
          <div className="py-4 text-center text-sm text-gray-500">
            Este pago aún no tiene análisis IA.
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cerrar</Button>

          {!tieneAnalisis && (
            <Button onClick={handleAnalizar} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-ia-analizar-ahora">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Analizar ahora
            </Button>
          )}

          {tieneAnalisis && !editMode && (
            <>
              <Button variant="outline" onClick={handleAnalizar} disabled={busy} data-testid="btn-ia-reanalizar">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Re-analizar
              </Button>
              <Button onClick={() => setEditMode(true)} disabled={busy}
                      className="bg-blue-600 hover:bg-blue-700" data-testid="btn-ia-editar">
                <Pencil className="w-4 h-4 mr-2" /> Corregir análisis
              </Button>
              <Button variant="destructive" onClick={handleRechazar} disabled={busy} data-testid="btn-ia-rechazar">
                <XCircle className="w-4 h-4 mr-2" /> Rechazar propuesta
              </Button>
            </>
          )}

          {tieneAnalisis && editMode && (
            <>
              <Button variant="outline" onClick={() => setEditMode(false)} disabled={busy}>
                Cancelar edición
              </Button>
              <Button onClick={handleGuardarEdicion} disabled={busy}
                      className="bg-green-600 hover:bg-green-700" data-testid="btn-ia-guardar-edicion">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar revisión
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AnalisisIATasasModal;
